import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Headphones, LineChart, Shield, ShieldAlert, UserPlus, Briefcase, Ticket } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { api } from "@/lib/api";

function fmtINR(v) {
  if (!Number.isFinite(v) || v === 0) return "₹0";
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)} Cr`;
  if (v >= 100_000) return `₹${(v / 100_000).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const ACTIVITY_ICON = { user_registered: UserPlus, otc_inquiry: Briefcase };

function QueueLink({ to, icon: Icon, title, hint, value, tone }) {
  return (
    <Link
      to={to}
      className="flex w-full min-w-0 items-center gap-3 overflow-hidden rounded-xl border border-border bg-background/40 px-3 py-3 text-left transition hover:border-primary/40"
    >
      <Icon className={`h-4 w-4 shrink-0 ${tone}`} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block truncate text-xs text-muted-foreground">{hint}</span>
      </span>
      {value === undefined ? (
        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : (
        <span className={`num shrink-0 text-lg font-semibold ${tone}`}>{value}</span>
      )}
    </Link>
  );
}

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get("/api/admin/dashboard/kpis").then(({ data }) => setKpis(data)).catch(() => {});
    api.get("/api/admin/dashboard/recent-activity").then(({ data }) => setActivity(data || [])).catch(() => {});
  }, []);

  const pending = kpis?.pending_kyc ?? 0;
  const tickets = kpis?.open_tickets ?? 0;
  const alerts = kpis?.compliance_alerts ?? 0;

  return (
    <AdminShell title="Dashboard" subtitle="Traders, desks and support in one reading.">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-2 xl:grid-cols-4" data-testid="kpi-row-1">
          <KpiCard label="Total users" value={kpis?.total_users ?? "—"} deltaLabel={kpis ? `+${kpis.new_users_last_month ?? 0} in the last 30 days` : ""} testid="kpi-total-users" />
          <KpiCard label="OTC volume 24h" value={fmtINR(kpis?.trading_volume_24h_inr)} testid="kpi-volume" />
          <KpiCard label="Fee revenue today" value={fmtINR(kpis?.revenue_today_inr)} testid="kpi-revenue" />
          <KpiCard label="New users today" value={kpis?.new_users_today ?? 0} variant="green" testid="kpi-new-users" />
        </div>
        <div className="grid grid-cols-2 gap-2 xl:grid-cols-4" data-testid="kpi-row-2">
          <KpiCard label="Pending KYC" value={pending} testid="kpi-kyc" />
          <KpiCard label="Open tickets" value={tickets} testid="kpi-tickets" />
          <KpiCard label="High-priority tickets" value={kpis?.high_priority_tickets ?? 0} variant={kpis?.high_priority_tickets ? "red" : undefined} />
          <KpiCard label="Compliance alerts" value={alerts} variant={alerts ? "red" : undefined} testid="kpi-compliance" />
        </div>

        <div className="grid min-w-0 items-start gap-5 lg:grid-cols-12">
          <section className="min-w-0 rounded-xl border border-border bg-surface/50 lg:col-span-7">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Recent activity</p>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-up">
                <span className="h-1.5 w-1.5 rounded-full bg-up" /> Live
              </span>
            </div>
            <div className="divide-y divide-border/70" data-testid="recent-activity">
              {activity.length === 0 ? (
                <p className="px-4 py-12 text-center text-sm text-muted-foreground">No recent activity.</p>
              ) : activity.slice(0, 8).map((a, i) => {
                const Icon = ACTIVITY_ICON[a.type] || Ticket;
                return (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface-2 text-primary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{a.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{a.subtitle}</p>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(a.at)}</span>
                  </div>
                );
              })}
            </div>
          </section>

          <aside className="min-w-0 rounded-xl border border-border bg-surface/50 p-5 lg:col-span-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Attention</p>
                <h2 className="mt-1 text-xl font-semibold">What needs a decision</h2>
              </div>
              <span className="shrink-0 rounded-full border border-border bg-surface-2 px-2.5 py-1 text-[11px] text-muted-foreground">
                {pending + tickets + alerts} open
              </span>
            </div>
            <div className="mt-5 space-y-2">
              <QueueLink to="/admin/kyc" icon={Shield} title="KYC review" hint="Aadhaar, PAN, face match and bank" value={pending} tone="text-warning" />
              <QueueLink to="/admin/support" icon={Headphones} title="Support queue" hint="Contact tickets still open" value={tickets} tone="text-down" />
              <QueueLink to="/admin/aml" icon={ShieldAlert} title="Compliance alerts" hint="AML alerts pending review" value={alerts} tone="text-down" />
              <QueueLink to="/admin/markets" icon={LineChart} title="Markets" hint="Prices, coin listing and P2P / OTC activity" tone="text-primary" />
            </div>
          </aside>
        </div>
      </div>
    </AdminShell>
  );
}
